import React, { useEffect, useRef, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { saveContent, formatApiError, triggerRebuild } from "../lib/api";
import {
  applyTheme, safeHex, hexToChannels,
  DEFAULT_THEME, DEFAULT_FONTS, HEADING_FONTS, BODY_FONTS,
} from "../lib/theme";

const COLOR_FIELDS = [
  { key: "sage", label: "Primary (buttons & accents)" },
  { key: "sageDark", label: "Primary hover / dark accent" },
  { key: "ocean", label: "Secondary accent (eyebrows)" },
  { key: "cream", label: "Page background" },
  { key: "cream2", label: "Card / section background" },
  { key: "sand", label: "Borders & dividers" },
  { key: "ink", label: "Headings / dark text" },
  { key: "inkSoft", label: "Body text" },
  { key: "inkMuted", label: "Muted text" },
  { key: "peach", label: "Decorative accent" },
  { key: "peachLight", label: "Decorative accent (light)" },
];

const ColorRow = ({ field, value, onChange }) => {
  const fallback = DEFAULT_THEME[field.key] || "#000000";
  const raw = value ?? fallback;
  const invalid = hexToChannels(raw) === null;
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <label className="block text-sm font-medium text-ink">{field.label}</label>
        {invalid && <p className="text-xs text-red-600">Enter a valid hex, e.g. #7A8F77</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="color"
          aria-label={`${field.label} swatch`}
          data-testid={`field-theme.${field.key}-swatch`}
          value={safeHex(raw, fallback)}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 rounded-md border border-cream-2 bg-white cursor-pointer p-0.5"
        />
        <input
          type="text"
          data-testid={`field-theme.${field.key}`}
          value={raw}
          onChange={(e) => onChange(e.target.value)}
          className={`w-28 rounded-lg border px-2 py-1.5 text-sm font-mono text-ink outline-none bg-white ${invalid ? "border-red-400 focus:border-red-500" : "border-cream-2 focus:border-sage"}`}
        />
      </div>
    </div>
  );
};

export const ThemeEditor = () => {
  const { content, setContent } = useContent();
  const [theme, setTheme] = useState(() => ({
    ...DEFAULT_THEME,
    fontHeading: DEFAULT_FONTS.heading,
    fontBody: DEFAULT_FONTS.body,
    ...(content?.theme || {}),
  }));
  const [saving, setSaving] = useState(false);

  const setKey = (key, val) => setTheme((t) => ({ ...t, [key]: val }));

  // Live-preview theme changes.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Restore saved theme if we leave without saving.
  const savedRef = useRef(content?.theme);
  savedRef.current = content?.theme;
  useEffect(() => () => applyTheme(savedRef.current || {}), []);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveContent({ ...content, theme });
      setContent(saved);
      triggerRebuild("theme changed");
      toast.success("Theme saved — changes are live");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="theme-editor">
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-cream py-3 z-10">
        <div>
          <h1 className="font-serif text-3xl text-ink">Theme</h1>
          <p className="text-sm text-ink-muted">Change your colours and fonts. Preview updates live; Save to publish.</p>
        </div>
        <button data-testid="save-theme-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>

      <div className="rounded-xl border border-cream-2 bg-white p-5 md:p-6 mb-4">
        <h2 className="font-serif text-xl text-ink mb-4">Colours</h2>
        <div className="space-y-4">
          {COLOR_FIELDS.map((f) => (
            <ColorRow key={f.key} field={f} value={theme[f.key]} onChange={(v) => setKey(f.key, v)} />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-cream-2 bg-white p-5 md:p-6">
        <h2 className="font-serif text-xl text-ink mb-4">Fonts</h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Heading font</label>
            <select
              data-testid="theme-font-heading"
              value={theme.fontHeading || DEFAULT_FONTS.heading}
              onChange={(e) => setKey("fontHeading", e.target.value)}
              className="w-full rounded-lg border border-cream-2 px-3 py-2.5 text-sm text-ink bg-white outline-none focus:border-sage"
            >
              {HEADING_FONTS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <p className="mt-3 text-3xl text-ink leading-tight" style={{ fontFamily: `"${theme.fontHeading || DEFAULT_FONTS.heading}"` }}>
              Life is messy. We all deserve support.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Body font</label>
            <select
              data-testid="theme-font-body"
              value={theme.fontBody || DEFAULT_FONTS.body}
              onChange={(e) => setKey("fontBody", e.target.value)}
              className="w-full rounded-lg border border-cream-2 px-3 py-2.5 text-sm text-ink bg-white outline-none focus:border-sage"
            >
              {BODY_FONTS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <p className="mt-3 text-base text-ink-soft leading-relaxed" style={{ fontFamily: `"${theme.fontBody || DEFAULT_FONTS.body}"` }}>
              A warm, welcoming space to talk things through. This is how your body text will look across the website.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeEditor;
